import React, { memo } from 'react';
import { Box, Icon, IconButton, Tooltip } from '@chakra-ui/react';
import { mdiInformationOutline } from '@mdi/js';
import { CONTEXT_IDS_TEXT } from 'features/context-ids/constants';

interface SensitiveResourceIconProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  iconSize?: string;
  tooltipText?: string;
  isDecorative?: boolean;
}

/**
 * Icon component to indicate sensitive resources (Edge Preview/Live)
 * Shows a yellow information icon with tooltip explaining security implications
 */
export const SensitiveResourceIcon = memo<SensitiveResourceIconProps>(
  ({
    size = 'md',
    iconSize = '20px',
    tooltipText = CONTEXT_IDS_TEXT.SENSITIVE_RESOURCE_TOOLTIP,
    isDecorative = false,
  }) => {
    const iconEl = (
      <Icon color='yellow.500' w={iconSize} h={iconSize}>
        <path d={mdiInformationOutline} />
      </Icon>
    );
    if (isDecorative) {
      return (
        <Tooltip
          label={tooltipText}
          placement='top'
          bg='gray.700'
          color='white'
        >
          <Box as='span' display='inline-flex' tabIndex={-1} aria-hidden='true'>
            <Icon color='yellow.500' w={iconSize} h={iconSize}>
              <path d={mdiInformationOutline} />
            </Icon>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip label={tooltipText} placement='top'>
        <IconButton
          aria-label={CONTEXT_IDS_TEXT.SENSITIVE_RESOURCE_WARNING}
          icon={iconEl}
          size={size}
          variant='ghost'
          minW='auto'
          h='auto'
          p={0}
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
          _focus={{ outline: 'none' }}
          _focusVisible={{
            outline: '2px solid var(--colors-primary-500)',
            outlineOffset: '2px',
          }}
        />
      </Tooltip>
    );
  },
);

SensitiveResourceIcon.displayName = 'SensitiveResourceIcon';
